import { useMany } from '@refinedev/core';
import type {
  CategoryDetailOut,
  ManagedCollectionDetailOut,
  PublicationListOut,
  TagDetailOut,
} from '@/api/generated/schemas';

export interface ClassificationNames {
  categories: Map<number, string>;
  collections: Map<number, string>;
  tags: Map<number, string>;
}

const uniqueIds = (
  rows: PublicationListOut[],
  pick: (r: PublicationListOut) => number[] | undefined
) => [...new Set(rows.flatMap((r) => pick(r) ?? []))];

const toMap = (items: { id: number; name?: string | null }[] | undefined) =>
  new Map((items ?? []).map((item) => [item.id, item.name ?? `#${item.id}`]));

/**
 * `PublicationListOut` carries only membership ids — resolve names for the
 * current page in three deduplicated batch requests (tooltip content only).
 */
export const useClassificationNames = (
  rows: PublicationListOut[]
): ClassificationNames => {
  const categoryIds = uniqueIds(rows, (r) => r.category_ids);
  const collectionIds = uniqueIds(rows, (r) => r.collection_ids);
  const tagIds = uniqueIds(rows, (r) => r.custom_tag_ids);

  const { result: categories } = useMany<CategoryDetailOut>({
    resource: 'user_catalog.categories',
    ids: categoryIds,
    queryOptions: { enabled: categoryIds.length > 0 },
  });
  const { result: collections } = useMany<ManagedCollectionDetailOut>({
    resource: 'user_catalog.managed_collections',
    ids: collectionIds,
    queryOptions: { enabled: collectionIds.length > 0 },
  });
  const { result: tags } = useMany<TagDetailOut>({
    resource: 'user_catalog.tags',
    ids: tagIds,
    queryOptions: { enabled: tagIds.length > 0 },
  });

  return {
    categories: toMap(categories.data),
    collections: toMap(collections.data),
    tags: toMap(tags.data),
  };
};
